// Hinweisleiste für die nächste Infoveranstaltung (Daniel 22.10.2026: „einen Hinweis für die nächste
// Info-Veranstaltung … mit direktem Link zur Landingpage“). Ein schmaler Streifen unter dem Menü der Webseite (alt und
// neu) und ein Hinweis in der Übersicht des Mitgliederbereichs. Welches Thema dort steht, schaltet die Administration
// je Thema („In der Hinweisleiste zeigen“, Spalte `in_leiste`); gelesen wird nur über `marketing_info_hinweis()`.
// Seit dem Abend des 22.10.2026 je Thema auch mit Countdown, eigener Zeile und einmaligem Pop-up.
// Ohne Importe, damit Webseite und Mitgliederbereich die Regeln nehmen können, ohne etwas mitzuladen. Regeln:
// docs/regeln/admin-dashboard.md „Hinweisleiste“.

/**
 * Nächster Termin für Hinweisleiste und Pop-up, wie `marketing_info_hinweis()` ihn liefert (seit 22.10.2026 mit
 * Countdown, eigener Zeile für die Leiste und Pop-up; `marketing_info_leiste()` bleibt für ältere Fassungen stehen).
 */
export interface InfoLeisteTermin {
  termin_id: string;
  slug: string;
  titel: string;
  beginn: string;
  untertitel: string | null;
  bild_url: string | null;
  dauer_minuten: number;
  countdown: boolean;
  leiste_text: string | null;
  popup: boolean;
  popup_titel: string | null;
  popup_text: string | null;
}

type Roh = Record<string, unknown>;
const text = (w: unknown): string | null => (typeof w === "string" && w.trim() ? w.trim() : null);

/** Erste brauchbare Zeile der Antwort, sonst `null` (dann zeigt niemand etwas). */
export function leisteLesen(daten: unknown): InfoLeisteTermin | null {
  for (const z of Array.isArray(daten) ? daten : []) {
    const o = (z ?? {}) as Roh;
    const termin_id = text(o.termin_id);
    const slug = text(o.slug);
    const titel = text(o.titel);
    const beginn = text(o.beginn);
    if (termin_id && slug && titel && beginn && !Number.isNaN(new Date(beginn).getTime())) {
      return {
        termin_id,
        slug,
        titel,
        beginn,
        untertitel: text(o.untertitel),
        bild_url: text(o.bild_url)?.startsWith("https://") ? text(o.bild_url) : null,
        dauer_minuten: typeof o.dauer_minuten === "number" && o.dauer_minuten > 0 ? o.dauer_minuten : 60,
        countdown: o.countdown === true,
        leiste_text: text(o.leiste_text),
        popup: o.popup === true,
        popup_titel: text(o.popup_titel),
        popup_text: text(o.popup_text),
      };
    }
  }
  return null;
}

/** Kurzer Titel fürs Handy: der Teil vor dem Doppelpunkt („Vorhang auf“). */
export function kurzTitel(titel: string): string {
  const teil = titel.split(":")[0]?.trim();
  return teil && teil.length >= 3 ? teil : titel.trim();
}

function uhrzeit(d: Date): string {
  const [stunde, minute] = d
    .toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" })
    .split(":");
  return `${minute === "00" ? String(Number(stunde)) : `${stunde}:${minute}`} Uhr`;
}

/** „Mo., 2. Nov., 20 Uhr“ für den schmalen Streifen. */
export function leisteZeitKurz(beginn: string): string {
  const d = new Date(beginn);
  const tag = d.toLocaleDateString("de-DE", { weekday: "short", day: "numeric", month: "short", timeZone: "Europe/Berlin" });
  return `${tag}, ${uhrzeit(d)}`;
}

/** „Mo., 2. Nov.“ für den Streifen am Handy (die Uhrzeit steht auf der Landingpage). */
export function leisteTagKurz(beginn: string): string {
  return new Date(beginn).toLocaleDateString("de-DE", {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "Europe/Berlin",
  });
}

/** „2. Nov., 20 Uhr“ für die Dachzeile der Leiste am Handy (ohne Wochentag, damit es auch bei 360 px passt). */
export function leisteTagUhrKnapp(beginn: string): string {
  const d = new Date(beginn);
  const tag = d.toLocaleDateString("de-DE", { day: "numeric", month: "short", timeZone: "Europe/Berlin" });
  return `${tag}, ${uhrzeit(d)}`;
}

/**
 * Was überall steht, wo für eine Infoveranstaltung geworben wird (Daniel 23.10.2026: „Außerdem muss da kostenlos
 * anmelden stehen, wenn wir sowas machen, grundsätzlich. Und da muss irgendwie klar sein, dass es sich um eine
 * Infoveranstaltung handelt.“): die Dachzeile in Leiste und Hinweis und der Text jedes Knopfs zur Anmeldung.
 */
export const INFO_DACH = "Kostenlose Infoveranstaltung";
export const INFO_KNOPF = "Kostenlos anmelden";

/** „Montag, 2. November, 20 Uhr“ für den Hinweis im Mitgliederbereich. */
export function leisteZeitLang(beginn: string): string {
  const d = new Date(beginn);
  const tag = d.toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long", timeZone: "Europe/Berlin" });
  return `${tag}, ${uhrzeit(d)}`;
}

/** Wo der Klick hinführt: die Landingpage mit dem Termin und der Stelle, von der aus geklickt wurde (`von`). */
export function leisteAdresse(
  t: Pick<InfoLeisteTermin, "slug" | "termin_id">,
  von: "leiste" | "mitgliederbereich" | "popup",
): string {
  const params = new URLSearchParams({ termin: t.termin_id, von });
  return `/info/${encodeURIComponent(t.slug)}?${params.toString()}`;
}

/** Auf der Landingpage selbst steht keine Leiste. */
export function leisteAufPfad(pfad: string): boolean {
  return !pfad.startsWith("/info/");
}

/**
 * Wo das Pop-up nie aufgeht: auf der Landingpage selbst, beim Anmelden und Registrieren, in Kassen, auf Dankeseiten
 * und auf den Rechtstexten (dort stört es, wer kaufen, sich anmelden oder etwas nachlesen will).
 */
const OHNE_POPUP = [
  "/info/",
  "/login",
  "/registrieren",
  "/auth/",
  "/abo-checkout",
  "/checkout",
  "/termin-checkout",
  "/themenreise-checkout",
  "/kasse",
  "/ausbildung/",
  "/abo-erfolgreich",
  "/kauf-erfolgreich",
  "/musik-kita-paket",
  "/themenreise-erfolgreich",
  "/shopify-danke",
  "/vertrag-widerrufen",
  "/widerruf",
  "/agb",
  "/datenschutz",
  "/impressum",
  "/webinar/",
  "/musikonzeptag/anmeldebestaetigung",
];

export function popupAufPfad(pfad: string): boolean {
  return !OHNE_POPUP.some((p) => pfad === p || pfad.startsWith(p.endsWith("/") ? p : `${p}/`) || pfad.startsWith(`${p}?`));
}

/** Schlüssel im Browser, wenn jemand das Pop-up für diesen Termin gesehen hat (es kommt je Termin nur einmal). */
export const popupGesehenSchluessel = (terminId: string) => `mk-infopopup-gesehen:${terminId}`;

/** Wie lange die Seite steht, bevor das Pop-up aufgeht. */
export const POPUP_VERZOEGERUNG_MS = 6000;

export interface CountdownTeile {
  tage: number;
  stunden: number;
  minuten: number;
  sekunden: number;
}

/** Was bis zum Beginn bleibt, in ganzen Tagen, Stunden, Minuten und Sekunden; `null`, wenn es schon begonnen hat. */
export function countdownTeile(beginn: string, jetzt: number = Date.now()): CountdownTeile | null {
  const rest = Math.floor((new Date(beginn).getTime() - jetzt) / 1000);
  if (!Number.isFinite(rest) || rest <= 0) return null;
  return {
    tage: Math.floor(rest / 86400),
    stunden: Math.floor((rest % 86400) / 3600),
    minuten: Math.floor((rest % 3600) / 60),
    sekunden: rest % 60,
  };
}

/** Zweistellig für die Kacheln: 5 → „05“. */
export const zweistellig = (n: number) => String(n).padStart(2, "0");

/** Schlüssel im Browser, wenn jemand die Leiste für diesen Termin geschlossen hat (gilt für Leiste und Hinweis). */
export const leisteWegSchluessel = (terminId: string) => `mk-infoleiste-weg:${terminId}`;
